/**
 * Lista de formularios de un embarque. Carga datos del backend,
 * aplica filtros/búsqueda, calcula totales y escucha actualizaciones
 * en tiempo real via WebSocket.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Minus, Plus } from 'lucide-react';
import { apiRequest } from '@/services/apiClient';
import { useSocket, type FormsUpdatedPayload } from '@/hooks/use-socket';
import FormCard from './FormCard';
import type { SearchFilters } from './SearchBar';
import { EMPTY_FILTERS } from './SearchBar';
import type { FormTotals } from './StatsBar';
import ConfirmDialog from '@/components/ConfirmDialog';
import styles from './FormList.module.css';

/** N.º de vehículo numérico en datos, o null si vacío / no numérico (sin id). */
function parseNumeroCoche(datos: FormData | undefined): number | null {
    const numStr = (datos?.numeroCoche ?? '').trim();
    if (!numStr) return null;
    const numValue = parseInt(numStr, 10);
    return Number.isNaN(numValue) ? null : numValue;
}

function compareByNumeroId(a: FormItem, b: FormItem): number {
    const aNum = parseNumeroCoche(a.datos);
    const bNum = parseNumeroCoche(b.datos);
    if (aNum === null && bNum === null) return a.id - b.id;
    if (aNum === null) return -1;
    if (bNum === null) return 1;
    if (aNum !== bNum) return aNum - bNum;
    return a.id - b.id;
}

interface FormData {
    vehiculos?: { coches?: string; motos?: string; camiones?: string; autobuses?: string; remolques?: string };
    pasajeros?: { adultos?: string; niños?: string; bebes?: string };
    numeroCoche?: string;
    tipoVehiculo?: string;
    cantidadVehiculos?: string;
    remolques?: string;
    codigoReservas: string[];
    anotaciones?: string;
    /** Campo legado en datos antiguos; ya no se edita en la UI. */
    comentarios?: string;
    embarqueId: string;
}

export interface FormItem {
    id: number;
    datos: FormData;
    completado: boolean;
    completado_secundario?: boolean;
    editado?: boolean;
    fecha_creacion: string;
    usuario_nombre?: string;
    usuario_username?: string;
}

interface FormListProps {
    searchTerm: string;
    filters?: SearchFilters;
    onEdit: (item: FormItem) => void;
    embarkationId: string;
    onTotalsChange?: (totals: FormTotals) => void;
    onCheckedTotalsChange?: (totals: FormTotals) => void;
    expandAll?: boolean;
}

export default function FormList({
    searchTerm,
    filters = EMPTY_FILTERS,
    onEdit,
    embarkationId,
    onTotalsChange,
    onCheckedTotalsChange,
    expandAll = false,
}: FormListProps) {
    const effectivePageSize = filters.pageSize || 0;
    const [forms, setForms] = useState<FormItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [visibleCount, setVisibleCount] = useState(effectivePageSize === 0 ? Infinity : effectivePageSize);
    const [formIdToDelete, setFormIdToDelete] = useState<number | null>(null);
    const fetchTokenRef = useRef(0);

    const fetchForms = useCallback(async () => {
        const tid = ++fetchTokenRef.current;
        setLoading(true);
        try {
            if (!embarkationId) {
                setForms([]);
                return;
            }
            const data = await apiRequest<{ success: boolean; forms?: FormItem[] }>(
                `/forms?embarkationId=${encodeURIComponent(embarkationId)}`,
            );
            if (tid !== fetchTokenRef.current) return;
            if (data.success && Array.isArray(data.forms)) {
                setForms(data.forms);
            } else {
                setForms([]);
            }
        } catch (error) {
            console.error('[FormList] Error fetching forms:', error);
            if (tid !== fetchTokenRef.current) return;
            setForms([]);
        } finally {
            if (tid === fetchTokenRef.current) setLoading(false);
        }
    }, [embarkationId]);

    useEffect(() => { void fetchForms(); }, [fetchForms]);

    const onSocketEvent = useCallback((event: FormsUpdatedPayload) => {
            if (event.action === 'create') setForms(prev => [event.data as unknown as FormItem, ...prev]);
            else if (event.action === 'update') {
                setForms(prev =>
                    prev.map(f =>
                        f.id === (event.data as unknown as FormItem).id ? (event.data as unknown as FormItem) : f,
                    ),
                );
            } else if (event.action === 'delete') {
                setForms(prev => prev.filter(f => f.id !== (event.data as unknown as FormItem).id));
            } else if (event.action === 'bulk_delete') setForms([]);
        },
        [],
    );

    useSocket(embarkationId, onSocketEvent, () => { void fetchForms(); });

    const openDeleteDialog = useCallback((id: number) => {
        setFormIdToDelete(id);
    }, []);

    const confirmDeleteForm = useCallback(async () => {
        if (formIdToDelete == null) return;
        const id = formIdToDelete;
        try {
            await apiRequest(`/forms/${id}`, { method: 'DELETE' });
            setForms(prev => prev.filter(f => f.id !== id));
        } catch (error) {
            console.error('[FormList] Error eliminando:', error);
        } finally {
            setFormIdToDelete(null);
        }
    }, [formIdToDelete]);

    const handleToggleStatus = useCallback(async (id: number, currentStatus: boolean) => {
        try {
            await apiRequest(`/forms/${id}/status`, { method: 'PATCH', body: { completado: !currentStatus } });
            setForms(prev => prev.map(f => f.id === id ? { ...f, completado: !currentStatus } : f));
        } catch (error) { console.error('[FormList] Error actualizando estado:', error); }
    }, []);

    const handleToggleSecondaryStatus = useCallback(async (id: number, currentStatus: boolean) => {
        try {
            await apiRequest(`/forms/${id}/status`, { method: 'PATCH', body: { completado_secundario: !currentStatus } });
            setForms(prev => prev.map(f => f.id === id ? { ...f, completado_secundario: !currentStatus } : f));
        } catch (error) { console.error('[FormList] Error actualizando estado secundario:', error); }
    }, []);

    const filteredForms = useMemo(() => {
        const filtered = forms.filter(item => {
        /* Pendientes = aún no tienen los dos controles (reloj/check) marcados */
        const ambosCompletados = item.completado === true && item.completado_secundario === true;
        if (filters.showOnlyPending && ambosCompletados) return false;
        const numStr = item.datos?.numeroCoche || '';
        const numValue = parseNumeroCoche(item.datos);
        const isNumeric = numValue !== null;
        for (const nf of filters.numeroFilter) {
            if (nf === 'par' && (!isNumeric || numValue % 2 !== 0)) return false;
            if (nf === 'impar' && (!isNumeric || numValue % 2 === 0)) return false;
            if (nf === 'sin_numero' && isNumeric) return false;
        }
        if (filters.numeroMin !== null && (!isNumeric || numValue < filters.numeroMin)) return false;
        if (filters.numeroMax !== null && (!isNumeric || numValue > filters.numeroMax)) return false;
        const search = searchTerm.toLowerCase().trim();
        if (search) {
            const numero = numStr.toLowerCase();
            const comentarios = item.datos?.comentarios?.toLowerCase() || "";
            const anotacionesTxt = (item.datos?.anotaciones || "").toLowerCase();
            const reservas = (item.datos?.codigoReservas || []).join(" ").toLowerCase();
            const nombre = item.usuario_nombre?.toLowerCase() || "";
            const username = item.usuario_username?.toLowerCase() || "";
            if (!numero.includes(search) && !comentarios.includes(search) && !anotacionesTxt.includes(search) && !reservas.includes(search) && !nombre.includes(search) && !username.includes(search)) return false;
        }
        return true;
        });
        if (filters.sortById) {
            const sign = filters.sortById === 'asc' ? 1 : -1;
            return [...filtered].sort((a, b) => sign * compareByNumeroId(a, b));
        }
        return filtered;
    }, [forms, filters, searchTerm]);

    useEffect(() => { setVisibleCount(effectivePageSize === 0 ? Infinity : effectivePageSize); }, [searchTerm, filters, embarkationId, effectivePageSize]);

    const visibleForms = useMemo(() => filteredForms.slice(0, visibleCount), [filteredForms, visibleCount]);
    const hasMore = visibleCount < filteredForms.length;

    const accumulateForm = (acc: FormTotals, d: Record<string, unknown>) => {
        if (d.vehiculos && typeof d.vehiculos === 'object') {
            const v = d.vehiculos as Record<string, string>;
            acc.vehiculos += parseInt(v.coches || '0', 10) || 0;
            acc.motos += parseInt(v.motos || '0', 10) || 0;
            acc.camiones += parseInt(v.camiones || '0', 10) || 0;
            acc.autobuses += parseInt(v.autobuses || '0', 10) || 0;
            acc.remolques += parseInt(v.remolques || '0', 10) || 0;
        } else {
            const tipo = ((d.tipoVehiculo as string) || 'coche').toLowerCase();
            const cant = parseInt((d.cantidadVehiculos as string) || '1', 10) || 1;
            if (tipo === 'moto') acc.motos += cant;
            else if (tipo === 'camion') acc.camiones += cant;
            else if (tipo === 'autobus') acc.autobuses += cant;
            else if (tipo === 'remolque') acc.remolques += cant;
            else acc.vehiculos += cant;
            acc.remolques += parseInt((d.remolques as string) || '0', 10) || 0;
        }
        const p = (d.pasajeros || {}) as Record<string, string>;
        acc.adultos += parseInt(p.adultos || '0', 10) || 0;
        acc.niños += parseInt(p.niños || '0', 10) || 0;
        acc.bebes += parseInt(p.bebes || '0', 10) || 0;
    };

    const emptyTotals = (): FormTotals => ({ vehiculos: 0, motos: 0, camiones: 0, autobuses: 0, adultos: 0, niños: 0, bebes: 0, remolques: 0 });

    const totals = useMemo<FormTotals>(() => {
        return forms.reduce((acc, item) => {
            accumulateForm(acc, (item.datos || {}) as unknown as Record<string, unknown>);
            return acc;
        }, emptyTotals());
    }, [forms]);

    const checkedTotals = useMemo<FormTotals>(() => {
        return forms.filter(f => f.completado).reduce((acc, item) => {
            accumulateForm(acc, (item.datos || {}) as unknown as Record<string, unknown>);
            return acc;
        }, emptyTotals());
    }, [forms]);

    useEffect(() => { onTotalsChange?.(totals); }, [totals, onTotalsChange]);
    useEffect(() => { onCheckedTotalsChange?.(checkedTotals); }, [checkedTotals, onCheckedTotalsChange]);

    if (loading) {
        return <div className={styles.center}><div className="spinner" /></div>;
    }

    return (
        <div className={styles.container}>
            <ConfirmDialog
                open={formIdToDelete != null}
                title="Eliminar formulario"
                onCancel={() => setFormIdToDelete(null)}
                onConfirm={confirmDeleteForm}
            >
                <p>Esta acción no se puede deshacer.</p>
            </ConfirmDialog>
            {filteredForms.length === 0 ? (
                <p className={styles.emptyText}>
                    {(searchTerm || filters !== EMPTY_FILTERS) ? "No se encontraron resultados." : "No hay registros guardados."}
                </p>
            ) : (
                <>
                    {visibleForms.map(item => (
                        <FormCard
                            key={item.id}
                            item={item}
                            onDelete={openDeleteDialog}
                            onToggleStatus={handleToggleStatus}
                            onToggleSecondaryStatus={handleToggleSecondaryStatus}
                            onEdit={onEdit}
                            forceExpand={!expandAll}
                        />
                    ))}
                    {(hasMore || (effectivePageSize > 0 && visibleCount > effectivePageSize)) && (
                        <div className={styles.paginationRow}>
                            {effectivePageSize > 0 && visibleCount > effectivePageSize && (
                                <button className={styles.paginationButton} onClick={() => setVisibleCount(effectivePageSize)}>
                                    <Minus size={20} color="white" />
                                </button>
                            )}
                            {hasMore && (
                                <button className={styles.paginationButton} onClick={() => setVisibleCount(prev => prev + effectivePageSize)}>
                                    <Plus size={20} color="white" />
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
